import React, { useEffect, useState } from 'react';
import { FaSearch, FaEdit, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

function SubcategoryOfferList() {
    const [offers, setOffers] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Form states
    const [editId, setEditId] = useState(null);
    const [subCategoryId, setSubCategoryId] = useState('');
    const [offerName, setOfferName] = useState('');
    const [discountPercentage, setDiscountPercentage] = useState('');

    const fetchOffers = async () => {
        const token = localStorage.getItem('authToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/get-offers`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const result = await res.json();
            setOffers(result.offers || []);
        } catch (error) {
            console.error('Error fetching offers:', error);
        }
    };

    const fetchCategories = async () => {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/get-category`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error("Failed to fetch categories");

            const result = await response.json();

            const subCategoryList = result.categories.flatMap((category) => {
                const raw = category.SubCategories;
                let parsedSubCategories = [];

                if (typeof raw === "string") {
                    try {
                        parsedSubCategories = JSON.parse(raw);
                    } catch (error) {
                        const matches = raw.match(/{[^{}]*}/g);
                        if (matches && matches.length > 0) {
                            parsedSubCategories = matches
                                .map((objStr) => {
                                    try {
                                        return JSON.parse(objStr);
                                    } catch {
                                        return null;
                                    }
                                })
                                .filter(Boolean);
                        }
                    }
                }

                return parsedSubCategories.map((sub) => ({
                    id: sub?.SubCategoryId,
                    name: sub?.SubCategoryName,
                    icon: sub?.SubCategoryIcon,
                    superCategoryId: category.SuperCategoryId,
                    superCategoryName: category.SuperCategoryName,
                }));
            });

            setSubCategories(subCategoryList);
        } catch (error) {
            console.error("❌ Error fetching categories:", error);
        }
    };

    useEffect(() => {
        fetchOffers();
        fetchCategories();
    }, []);

    const resetForm = () => {
        setEditId(null);
        setSubCategoryId('');
        setOfferName('');
        setDiscountPercentage('');
        setIsEditing(false);
    };

    const handleAddOrEditOffer = async () => {
        const token = localStorage.getItem('authToken');

        if (!subCategoryId || !offerName || discountPercentage === '') {
            alert('Please fill all fields');
            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/offers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    SubCategoryId: subCategoryId,
                    OfferName: offerName,
                    DiscountPercentage: parseFloat(discountPercentage),
                }),
            });

            if (!res.ok) throw new Error('Failed to add/update offer');

            await fetchOffers();
            setShowModal(false);
            resetForm();
        } catch (err) {
            console.error(err);
        }
    };

    const handleEditClick = (offer) => {
        setEditId(offer.subcategory_offers_id);
        setSubCategoryId(offer.SubCategoryId);
        setOfferName(offer.OfferName);
        setDiscountPercentage(offer.DiscountPercentage);
        setIsEditing(true);
        setShowModal(true);
    };

    const filteredOffers = offers.filter((offer) =>
        Object.values(offer).some((value) =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const currentOffers = filteredOffers.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
    const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);

    const handlePageChange = (page) => setCurrentPage(page);

    return (
        <div className="container" style={{ marginTop: '80px' }}>
            <h2 className="text-primary">Subcategory Offers</h2>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="input-group me-3" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text bg-primary text-white">
                        <FaSearch />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search offers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-success" onClick={() => { resetForm(); setShowModal(true); }}>
                    <FaPlus /> Add Offer
                </button>
            </div>

            <table className="table table-bordered table-striped">
                <thead className="table-primary">
                    <tr>
                        <th>ID</th>
                        <th>Subcategory</th>
                        <th>Offer Name</th>
                        <th>Discount (%)</th>
                        <th>Created At</th>
                        <th>Updated At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentOffers.map((offer) => {
                        const subcat = subCategories.find(sub => sub.id === offer.SubCategoryId);
                        return (
                            <tr key={offer.subcategory_offers_id}>
                                <td>{offer.subcategory_offers_id}</td>
                                <td>{subcat ? subcat.name : offer.SubCategoryId}</td>
                                <td>{offer.OfferName}</td>
                                <td>{offer.DiscountPercentage}%</td>
                                <td>{new Date(offer.CreatedAt).toLocaleString()}</td>
                                <td>{new Date(offer.UpdatedAt).toLocaleString()}</td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleEditClick(offer)}
                                    >
                                        <FaEdit /> Edit
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <nav>
                <ul className="pagination justify-content-center">
                    {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                                {i + 1}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Offer' : 'Add Offer'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Subcategory</Form.Label>
                            <Form.Select
                                value={subCategoryId}
                                onChange={(e) => {
                                    const selected = subCategories.find(sub => sub.id === parseInt(e.target.value));
                                    setSubCategoryId(selected?.id || '');
                                }}
                            >
                                <option value="">-- Select Subcategory --</option>
                                {subCategories.map((sub) => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Offer Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={offerName}
                                onChange={(e) => setOfferName(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Discount Percentage</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={discountPercentage}
                                onChange={(e) => setDiscountPercentage(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddOrEditOffer}>
                        {isEditing ? 'Update Offer' : 'Add Offer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default SubcategoryOfferList;
